import { Box, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { Badge, Card, DataTable, MISSING_VALUE } from '@/ui/kit';
import { fontStacks } from '@/ui/tokens';

import { stockSearchPath } from './routes';
import {
  batchCodes,
  describeMovementLocation,
  describeOperation,
  formatDelta,
  formatMoment,
  formatQuantity,
  movementBalances,
  shortId,
  type BalanceCell,
} from './stockPresentation';
import type { StockBatch, StockLineage, StockMovement } from './stockTypes';

/**
 * Mouvements d'un lot et de sa genealogie, dans l'ordre d'ecriture du journal.
 *
 * Le solde n'est calcule que pour **ce** lot (`movementBalances`) : un mouvement
 * d'un autre lot de l'arbre le nomme, avec un lien vers sa trace, et n'a pas de
 * solde ici. Quand la reconstitution echoue, la cellule le dit — « Non
 * évaluable » —, elle ne montre pas un nombre.
 *
 * « Par » donne l'adresse du compte qui a pousse l'evenement et l'identifiant
 * d'appareil **brut** : le serveur ne connait pas de nom d'appareil.
 */

interface Row {
  readonly index: number;
  readonly movement: StockMovement;
  readonly balance: BalanceCell;
}

const describePlace = ({ movement, batch }: { movement: StockMovement; batch: StockBatch }): string => {
  const source = describeMovementLocation(movement.sourceLocationId, batch);
  const destination = describeMovementLocation(movement.destinationLocationId, batch);

  if (movement.sourceLocationId !== null && movement.destinationLocationId !== null) {
    return movement.sourceLocationId === movement.destinationLocationId ? destination : `${source} → ${destination}`;
  }

  return movement.destinationLocationId !== null ? destination : source;
};

export const MovementsTable = ({
  batch,
  movements,
  lineage,
}: {
  batch: StockBatch;
  movements: readonly StockMovement[];
  lineage: StockLineage;
}): ReactElement => {
  const balances = movementBalances(batch, movements);
  const codes = batchCodes(batch, lineage);
  const rows: readonly Row[] = movements.map((movement, index) => ({
    index,
    movement,
    balance: balances[index] ?? { kind: 'not-evaluable' },
  }));

  return (
    <Card
      title="Mouvements"
      subtitle="Ceux de ce lot et des lots de sa généalogie, dans l’ordre d’écriture du journal."
    >
      <DataTable<Row>
        label="Mouvements du lot"
        rows={rows}
        getRowId={(row) => String(row.index)}
        emptyMessage="Aucun mouvement lisible pour ce lot."
        columns={[
          { key: 'when', header: 'Quand', primary: true, render: ({ movement }) => formatMoment(movement.occurredAt) },
          {
            key: 'operation',
            header: 'Opération',
            render: ({ movement }) => {
              const operation = describeOperation(movement.type);
              const other = movement.batchId !== null && movement.batchId !== batch.id ? movement.batchId : null;

              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Badge label={operation.label} tone={operation.tone} />
                  {other === null ? null : (
                    <Typography component="span" sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
                      sur{' '}
                      <Box
                        component={Link}
                        to={stockSearchPath(other)}
                        sx={{ color: 'inherit', fontFamily: fontStacks.mono, textUnderlineOffset: 3, overflowWrap: 'anywhere' }}
                      >
                        {codes.get(other) ?? `lot ${shortId(other)}`}
                      </Box>
                    </Typography>
                  )}
                </Box>
              );
            },
          },
          {
            key: 'quantity',
            header: 'Quantité',
            mono: true,
            render: ({ movement }) => formatDelta(movement.quantityDelta, movement.unit),
          },
          { key: 'place', header: 'Lieu', render: ({ movement }) => describePlace({ movement, batch }) },
          {
            key: 'by',
            header: 'Par',
            render: ({ movement }) => (
              <Box sx={{ minWidth: 0 }}>
                <Typography component="div" sx={{ fontSize: 14, overflowWrap: 'anywhere' }}>
                  {movement.actor.email ?? MISSING_VALUE}
                </Typography>
                {movement.originDeviceHash === null ? null : (
                  <Typography
                    component="div"
                    sx={{ fontSize: 12, color: 'tokens.textMuted', fontFamily: fontStacks.mono, overflowWrap: 'anywhere' }}
                  >
                    {movement.originDeviceHash}
                  </Typography>
                )}
              </Box>
            ),
          },
          {
            key: 'balance',
            header: 'Solde',
            mono: true,
            render: ({ balance }) =>
              balance.kind === 'value' ? (
                formatQuantity(balance.value, batch.unit)
              ) : balance.kind === 'not-evaluable' ? (
                <Badge label="Non évaluable" tone="neutral" />
              ) : null,
          },
        ]}
      />
    </Card>
  );
};
